import { stripTypologyPrefix } from '../../../../../../util/typology';

/**
 * FIN-11304 — the Typology / Score / Action shown beside the SCENARIO dropdown,
 * the same three values the Explanation table renders for that typology.
 *
 * The scenarios endpoint does NOT carry them. `AlertScenario` is
 * `{ typologyId, typologyName, eventCount }` and nothing more
 * (AlertScenariosResponse.scala:3), which is why ScenarioBar's variant and score
 * tags have never rendered on live data and why it had no Action tag at all.
 *
 * NO NEW API. All three already arrive for the Explanation tab, from the two
 * endpoints Classic uses:
 *   preventionScoreExplainability  -> { [typologyName]: [ { triggerName, action, … } ] }
 *   alertScoreExplanation          -> ruleContributions: [ { ruleName, effectiveScore } ]
 * This module joins them into a per-typology lookup the scenario bar can read.
 * It mirrors `toRules` in ExplanationPanel/index.js so the bar and the Explanation
 * table can never disagree about the same typology.
 *
 * THE JOIN KEY IS THE TYPOLOGY NAME, because that is the only field the two
 * sources share — `scoreExplainability` is keyed by name and carries no
 * typologyId, and the scenario carries no name-independent handle into it.
 *
 * KNOWN LIMITATION, and the reason this can render nothing today: the BE resolves
 * `typologyName` by joining StarRocks `triggered_typology` to MariaDB
 * `typologies.id`, and falls back to the literal `s"Typology $id"` when that
 * misses (StarRocksEventTimelineDao.scala:89-91). Those two id spaces are
 * currently disjoint, so every live scenario name IS that fallback and no join
 * can match. When it cannot match, this returns no entry and the tags stay hidden
 * — the behaviour before this module existed. It must never invent a score.
 */

// Normalised so the two sources' spelling of the same typology matches. Handles
// the AFC "Typology" prefix (FIN-11922) that only one side carries.
//
// The BE's `"Typology 123"` fallback normalises to `"123"`, which no
// scoreExplainability key can equal — so a fallback name cannot produce a FALSE
// match, it simply finds nothing. That is the intended outcome, not a near miss.
const key = (name) =>
  typeof name === 'string'
    ? stripTypologyPrefix(name).trim().toLowerCase()
    : '';

// `ruleContributions` is a JSON *string* on the wire. Parsed here rather than by
// the caller so this module is self-contained and a malformed payload degrades to
// "no scores" instead of throwing into the timeline, which is the primary content.
const parseContributions = (alertScoreExplanation) => {
  const raw = alertScoreExplanation?.ruleContributions;
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

/**
 * @param {object} scoreExplainability  amlsTMCommon.preventionScoreExplainability.data
 * @param {object} alertScoreExplanation amlsTMCommon.alertScoreExplanation.data
 * @returns {object} normalised typology name -> { variant, score, action, variantCount }
 */
export const toScenarioMeta = (scoreExplainability, alertScoreExplanation) => {
  const byName = scoreExplainability;
  if (!byName || typeof byName !== 'object') return {};

  const contributions = parseContributions(alertScoreExplanation);
  const scoreOf = (triggerName) => {
    const hit = contributions.find((c) => c && c.ruleName === triggerName);
    return typeof hit?.effectiveScore === 'number'
      ? hit.effectiveScore
      : undefined;
  };

  return Object.keys(byName).reduce((acc, typologyName) => {
    const triggers = (byName[typologyName] || []).filter(Boolean);
    if (!triggers.length) return acc;

    const scored = triggers.map((t) => ({
      variant: t.triggerName,
      action: t.action,
      score: scoreOf(t.triggerName)
    }));

    // A typology can breach SEVERAL variants, but the bar has room for one. Show
    // the highest-scoring — the rule that drove the scenario hardest — rather than
    // the first, which is just whatever order the payload arrived in.
    // `variantCount` is carried so the bar can say so instead of silently
    // implying this is the only variant that fired.
    const top = [...scored].sort(
      (a, b) => (b.score ?? -1) - (a.score ?? -1)
    )[0];

    const name = key(typologyName);
    if (!name) return acc;

    return {
      ...acc,
      [name]: {
        // The TYPOLOGY, as the Explanation table's first column shows it —
        // prefix-stripped for display exactly as `toRules` does it there
        // (stripTypologyPrefix), so the same alert reads identically in both
        // places. This is what the bar badges.
        typology: stripTypologyPrefix(typologyName).trim(),
        // The specific rule that fired. NOT badged — it is a near-duplicate of the
        // typology with a `_clone1` suffix and reads as noise beside it — but kept
        // so the bar can disclose it on hover, where the extra precision is free.
        variant: top.variant,
        action: top.action,
        // Kept unrounded; ScenarioBar formats it to 2dp like Classic's
        // Explanation table. Scenario scores are already 0-100 here
        // (rules[].score reads 45 / 37), NOT the 0-1 riskScore the timeline's
        // toScore() scales — running these through that would render 4500.
        score: typeof top.score === 'number' ? top.score : undefined,
        variantCount: triggers.length
      }
    };
  }, {});
};

/**
 * Merges the lookup onto a scenario list from toScenarioList, leaving entries
 * with no match exactly as they were.
 *
 * The "All events" entry is deliberately SKIPPED (it has `typologyId: null`): it
 * is not a typology, and badging it with one typology's score would misreport
 * every other scenario's contribution as belonging to the whole alert.
 */
export const withScenarioMeta = (scenarios, meta) => {
  if (!Array.isArray(scenarios) || !scenarios.length) return scenarios;
  if (!meta || !Object.keys(meta).length) return scenarios;

  return scenarios.map((s) => {
    if (!s || s.typologyId == null) return s;
    const hit = meta[key(s.name)];
    if (!hit) return s;
    // GAPS ONLY — never clobber. Since 2026-08-21 the scenarios endpoint supplies
    // typology and score itself, and that is the authoritative source; this module
    // is now a fallback for the fields it could not resolve. Spreading `hit` over
    // `s` would let a stale Explanation-derived value win over a live one.
    const filled = { ...hit };
    Object.keys(filled).forEach((k) => {
      if (s[k] != null) delete filled[k];
    });
    return { ...s, ...filled };
  });
};

export default toScenarioMeta;

const lower = (v) => (typeof v === 'string' ? v.toLowerCase() : v);

/**
 * FIN-13724 — colours each scenario's Action tag the way Classic's Explanation
 * table does (preventionTable.js): the rule's severity as scored
 * (triggers[].ruleSeverity), falling back to the Action Catalog's severity for
 * the action name, then that severity's colour from Level Name Setting
 * (alert_level_details). Scenarios that resolve no colour are left untouched,
 * so the tag keeps its stylesheet default.
 */
export const withActionColor = (
  scenarios,
  scoreExplainability,
  trmActions,
  alertLevels
) => {
  if (!Array.isArray(scenarios) || !scenarios.length) return scenarios;
  if (!Array.isArray(alertLevels) || !alertLevels.length) return scenarios;

  // Classic resolves severity per (typology, trigger) row, so key on the pair;
  // the variant name alone is the fallback for a scenario with no typology.
  const byTypology =
    scoreExplainability && typeof scoreExplainability === 'object'
      ? scoreExplainability
      : {};
  const pairKey = (typology, variant) => `${key(typology)}|${variant}`;
  const severityByPair = {};
  const severityByVariant = {};
  Object.keys(byTypology).forEach((typologyName) =>
    (byTypology[typologyName] || []).forEach((t) => {
      if (!t || !t.triggerName || !t.ruleSeverity) return;
      severityByPair[pairKey(typologyName, t.triggerName)] = t.ruleSeverity;
      severityByVariant[t.triggerName] = t.ruleSeverity;
    })
  );
  const catalogSeverity = (action) =>
    (Array.isArray(trmActions) ? trmActions : []).find(
      (a) => a && lower(a.displayName) === lower(action)
    )?.actionSeverity;
  const colorOf = (severity) =>
    alertLevels.find((l) => l && lower(l.code) === lower(severity))?.color;

  return scenarios.map((s) => {
    if (!s || !s.action) return s;
    const variant = s.variantName || s.variant;
    const severity =
      (s.typology ? severityByPair[pairKey(s.typology, variant)] : undefined) ??
      severityByVariant[variant] ??
      catalogSeverity(s.action);
    const actionColor = severity ? colorOf(severity) : undefined;
    return actionColor ? { ...s, actionColor } : s;
  });
};

/**
 * FIN-12857 — per-VARIANT meta, keyed by the variant name.
 *
 * The scenarios endpoint is supposed to carry score / typology itself, but its
 * lookup resolves only for variants whose most recent event happens to be a
 * transaction row carrying SCORE_CONTRIBUTIONS. Measured on QA-FRAML-001: 2 of 6.
 * The other 4 return `variantScore: 0.0` and `typologyName: ""` — activity-driven
 * variants can never resolve, because their last event key is an activity id and
 * `unified_alerts.TRANSACTION_KEY` holds only transaction keys.
 *
 * `alertScoreExplanation.ruleContributions` has no such problem: it lists EVERY
 * fired rule once, with its score, its action and its typology id. It is the same
 * payload the Explanation table scores from, and the FE already fetches it — so
 * this fills the gaps without a new request and without the two views being able
 * to disagree.
 *
 * THE JOIN KEY IS THE RULE NAME, and it is now reliable: StarRocks
 * `triggered_rule` and `SCORE_CONTRIBUTIONS[].ruleName` were seeded from two
 * independent lists until 2026-08-21 and shared no values at all. They are
 * reconciled in gen-framl-p-activity-tx-seed.py; if this ever returns nothing for
 * every variant, suspect that alignment first.
 *
 * REMOVE THIS once the BE resolves all variants (its own plan's simpler form —
 * read one scored row for the alert rather than one per variant — would do it,
 * since every scored row already lists all the rules).
 *
 * @returns {object} ruleName -> { score, action, typologyId }
 */
export const toVariantMeta = (alertScoreExplanation, scoreExplainability) => {
  const contributions = parseContributions(alertScoreExplanation);

  // variantName -> typologyName, by INVERTING scoreExplainability, which is keyed
  // by typology and lists that typology's triggers. It is the only source of a
  // typology NAME per variant: ruleContributions carries the typology ID but not
  // its name, and /scenarios leaves the name blank for every variant it cannot
  // resolve (both activity-driven ones on QA-FRAML-001).
  const typologyByVariant =
    scoreExplainability && typeof scoreExplainability === 'object'
      ? Object.keys(scoreExplainability).reduce((acc, typologyName) => {
          const triggers = scoreExplainability[typologyName];
          if (!Array.isArray(triggers)) return acc;
          const display = stripTypologyPrefix(typologyName).trim();
          if (!display) return acc;
          return triggers.reduce(
            (inner, t) =>
              t && typeof t.triggerName === 'string' && t.triggerName
                ? { ...inner, [t.triggerName]: display }
                : inner,
            acc
          );
        }, {})
      : {};

  return contributions.reduce((acc, c) => {
    if (!c || typeof c.ruleName !== 'string' || !c.ruleName) return acc;
    return {
      ...acc,
      [c.ruleName]: {
        // Already 0-100 here, NOT the 0-1 riskScore the timeline's toScore scales.
        score:
          typeof c.effectiveScore === 'number' ? c.effectiveScore : undefined,
        action: typeof c.action === 'string' && c.action ? c.action : undefined,
        typology: typologyByVariant[c.ruleName],
        // SCORE_CONTRIBUTIONS names this `ruleId`, but it holds the TYPOLOGY id —
        // two variants of one typology share it (201 covers both Velocity Abuse
        // rules). Used below to recover a typology NAME the endpoint did not send.
        typologyId: typeof c.ruleId === 'number' ? c.ruleId : undefined
      }
    };
  }, {});
};

/**
 * Merges per-variant meta onto the scenario list, GAPS ONLY — a value the
 * endpoint resolved always wins.
 *
 * Also recovers `typology` for entries the endpoint left blank, by taking the
 * name from a sibling entry with the same typology id. That is a lookup, not a
 * guess: the typology name is a function of the typology id, and both come from
 * the same alert's payload. Entries whose typology id appears nowhere with a name
 * keep no typology rather than inventing one.
 *
 * "All events" is skipped — it has no variantName and is not one variant.
 */
export const withVariantMeta = (scenarios, meta) => {
  if (!Array.isArray(scenarios) || !scenarios.length) return scenarios;
  if (!meta || !Object.keys(meta).length) return scenarios;

  const filled = scenarios.map((s) => {
    if (!s || !s.variantName) return s;
    const hit = meta[s.variantName];
    if (!hit) return s;
    return {
      ...s,
      score: s.score != null ? s.score : hit.score,
      action: s.action != null ? s.action : hit.action,
      typology: s.typology != null ? s.typology : hit.typology,
      typologyId: s.typologyId != null ? s.typologyId : hit.typologyId
    };
  });

  // typologyId -> typologyName, from whichever entries the endpoint DID resolve.
  const nameById = filled.reduce((acc, s) => {
    if (s && s.typologyId != null && s.typology && !acc[s.typologyId]) {
      return { ...acc, [s.typologyId]: s.typology };
    }
    return acc;
  }, {});

  if (!Object.keys(nameById).length) return filled;

  return filled.map((s) =>
    s && !s.typology && s.typologyId != null && nameById[s.typologyId]
      ? { ...s, typology: nameById[s.typologyId] }
      : s
  );
};
